import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import './BigTournaments.css';


const API_ORIGIN =
  process.env.REACT_APP_API_URL ||
  'http://localhost:5000';

const API_BASE =
  `${API_ORIGIN}/api`;


const LOCALES = {
  ru: 'ru-RU',
  en: 'en-GB',
  ka: 'ka-GE',
  hy: 'hy-AM',
  az: 'az-AZ',
  it: 'it-IT',
  fr: 'fr-FR',
};


function formatDateValue(value, locale) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString(
    locale,
    {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
  );
}


function formatCountdownValue(startAt, now, translate) {
  if (!startAt) return '';

  const target =
    new Date(startAt).getTime();

  const diff =
    target - now;

  if (diff <= 0) {
    return translate('bigTournaments.countdown.tournamentStarted');
  }

  const total =
    Math.floor(diff / 1000);

  const days =
    Math.floor(total / 86400);

  const hours =
    Math.floor(
      (total % 86400) / 3600
    );

  const minutes =
    Math.floor(
      (total % 3600) / 60
    );

  const seconds =
    total % 60;

  if (days > 0) {
    return translate('bigTournaments.countdown.days', {
      days,
      hours,
      minutes,
    });
  }

  if (hours > 0) {
    return translate('bigTournaments.countdown.hours', {
      hours,
      minutes,
      seconds,
    });
  }

  return translate('bigTournaments.countdown.minutes', {
    minutes,
    seconds: String(seconds).padStart(2, '0'),
  });
}



function formatRebuyCountdown(
  endsAt,
  now
) {
  if (!endsAt) {
    return '00:00';
  }

  const target =
    new Date(endsAt).getTime();

  const diff =
    Math.max(
      0,
      target - now
    );

  const totalSeconds =
    Math.ceil(diff / 1000);

  const minutes =
    Math.floor(
      totalSeconds / 60
    );

  const seconds =
    totalSeconds % 60;

  return (
    `${String(minutes).padStart(2, '0')}:` +
    `${String(seconds).padStart(2, '0')}`
  );
}


function parseArrayConfig(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed;
      }

      if (typeof parsed === 'string') {
        return parseArrayConfig(parsed);
      }
    } catch (_) {
      return [];
    }
  }

  return [];
}


function parseObjectConfig(value) {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);

      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed)
      ) {
        return parsed;
      }

      if (typeof parsed === 'string') {
        return parseObjectConfig(parsed);
      }
    } catch (_) {
      return null;
    }
  }

  return null;
}


function formatCreditsValue(value, locale) {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString(
    locale,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  );
}


function formatPrizePlacesValue(place, translate) {
  const from = Number(
    place?.from ??
    place?.place ??
    0
  );

  const to = Number(
    place?.to ??
    place?.place ??
    from
  );

  if (!from) {
    return translate('bigTournaments.details.placeNotSpecified');
  }

  if (from === to) {
    return translate('bigTournaments.details.singlePlace', {
      place: from,
    });
  }

  return translate('bigTournaments.details.placeRange', {
    from,
    to,
  });
}


function getRoundTitleValue(
  roundMatches,
  roundNumber,
  translate
) {
  if (!roundMatches?.length) {
    return translate('bigTournaments.details.roundNumber', {
      round: roundNumber,
    });
  }

  const stages =
    new Set(
      roundMatches.map(
        match => match.stage
      )
    );

  if (stages.has('qualification')) {
    return translate('bigTournaments.statuses.qualification');
  }

  if (stages.has('rebuy')) {
    return translate('bigTournaments.details.rebuyRoundNumber', {
      round: roundNumber,
    });
  }

  if (stages.has('final')) {
    return translate('bigTournaments.details.final');
  }

  const realMatches =
    roundMatches.filter(
      match => !match.is_bye
    );

  const count =
    realMatches.length;

  if (count === 2) {
    return translate('bigTournaments.details.semifinal');
  }

  if (count === 4) {
    return translate('bigTournaments.details.quarterfinal');
  }

  if (count === 8) {
    return translate('bigTournaments.details.roundOf16');
  }

  if (count === 16) {
    return translate('bigTournaments.details.roundOf32');
  }

  return translate('bigTournaments.details.roundNumber', {
    round: roundNumber,
  });
}


export default function BigTournamentDetails() {

  const { id } =
    useParams();

  const navigate =
    useNavigate();

  const { t: translate, i18n } =
    useTranslation();

  const languageCode = String(
    i18n.resolvedLanguage ||
    i18n.language ||
    'ru'
  )
    .split('-')[0]
    .toLowerCase();

  const locale =
    LOCALES[languageCode] ||
    LOCALES.ru;

  const statusLabels = {
    registration: translate('bigTournaments.statuses.registration'),
    starting: translate('bigTournaments.statuses.starting'),
    qualification: translate('bigTournaments.statuses.qualification'),
    bracket: translate('bigTournaments.statuses.bracket'),
    finished: translate('bigTournaments.statuses.finished'),
    cancelled: translate('bigTournaments.statuses.cancelled'),
    rebuy_round: translate('bigTournaments.statuses.rebuyRound'),
    rebuy_window: translate('bigTournaments.statuses.rebuyWindow'),
  };

  const playerStatusLabels = {
    registered: translate('bigTournaments.playerStatuses.registered'),
    active: translate('bigTournaments.playerStatuses.active'),
    waiting: translate('bigTournaments.playerStatuses.waiting'),
    bye: 'BYE',
    eliminated_rebuy: translate('bigTournaments.playerStatuses.eliminatedRebuy'),
    rebought: translate('bigTournaments.playerStatuses.rebought'),
    eliminated: translate('bigTournaments.playerStatuses.eliminated'),
    finished: translate('bigTournaments.playerStatuses.finished'),
  };

  const formatDate = (value) =>
    formatDateValue(value, locale);

  const formatCountdown = (
    startAt,
    currentTime
  ) =>
    formatCountdownValue(
      startAt,
      currentTime,
      translate
    );

  const formatCredits = (value) =>
    formatCreditsValue(value, locale);

  const formatPrizePlaces = (place) =>
    formatPrizePlacesValue(
      place,
      translate
    );

  const getRoundTitle = (
    roundMatches,
    roundNumber
  ) =>
    getRoundTitleValue(
      roundMatches,
      roundNumber,
      translate
    );


  const [tournament, setTournament] =
    useState(null);

  const [players, setPlayers] =
    useState([]);

  const [matches, setMatches] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [registering, setRegistering] =
    useState(false);

  const [rebuying, setRebuying] =
    useState(false);

  const [message, setMessage] =
    useState('');

  const [activeTab, setActiveTab] =
    useState('bracket');

  const [now, setNow] =
    useState(Date.now());


  const username =
    localStorage.getItem('username') || '';

  const token =
    localStorage.getItem('token') || '';


  const loadData = useCallback(async (
    silent = false
  ) => {
    try {

      if (!silent) {
        setLoading(true);
      }

      const [
        tournamentResponse,
        matchesResponse
      ] =
        await Promise.all([
          fetch(
            `${API_BASE}/big-tournaments/${id}`
          ),

          fetch(
            `${API_BASE}/big-tournaments/${id}/matches`
          ),
        ]);


      const tournamentData =
        await tournamentResponse.json();

      const matchesData =
        await matchesResponse.json();


      if (
        !tournamentResponse.ok ||
        !tournamentData?.ok
      ) {
        throw new Error(
          tournamentData?.error ||
          translate('bigTournaments.details.loadFailed')
        );
      }


      if (
        !matchesResponse.ok ||
        !matchesData?.ok
      ) {
        throw new Error(
          matchesData?.error ||
          translate('bigTournaments.details.bracketLoadFailed')
        );
      }


      setTournament(
        tournamentData.tournament
      );

      setPlayers(
        Array.isArray(
          tournamentData.players
        )
          ? tournamentData.players
          : []
      );

      setMatches(
        Array.isArray(
          matchesData.matches
        )
          ? matchesData.matches
          : []
      );

      setError('');

    } catch (e) {

      console.error(
        '[BIG TOURNAMENT DETAILS]',
        e
      );

      setError(
        e.message ||
        translate('bigTournaments.details.loadError')
      );

    } finally {

      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, translate]);


  useEffect(() => {

    loadData();

    const poll =
      setInterval(
        () => loadData(true),
        5000
      );

    const clock =
      setInterval(
        () => {
          setNow(Date.now());
        },
        1000
      );

    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };

  }, [loadData]);


  const amRegistered =
    useMemo(
      () =>
        players.some(
          player =>
            player.username ===
            username
        ),
      [players, username]
    );


  const myPlayer =
    useMemo(
      () =>
        players.find(
          player =>
            player.username ===
            username
        ) || null,
      [players, username]
    );


  const reboughtPlayers =
    useMemo(
      () =>
        players
          .filter(
            player =>
              Number(
                player.rebuy_count || 0
              ) > 0
          )
          .sort(
            (a, b) =>
              Number(b.rebuy_count || 0) -
              Number(a.rebuy_count || 0)
          ),
      [players]
    );


  const myActiveMatch =
    useMemo(
      () =>
        matches.find(
          match =>
            match.status === 'active' &&
            (
              match.player1_name === username ||
              match.player2_name === username
            )
        ) || null,
      [matches, username]
    );


  const rounds =
    useMemo(() => {

      const grouped =
        new Map();

      for (const match of matches) {

        const round =
          Number(
            match.round_number || 0
          );

        if (!grouped.has(round)) {
          grouped.set(round, []);
        }

        grouped
          .get(round)
          .push(match);
      }


      return Array
        .from(grouped.entries())
        .sort(
          (a, b) =>
            a[0] - b[0]
        )
        .map(
          ([roundNumber, roundMatches]) => ({
            roundNumber,
            matches: roundMatches,
          })
        );

    }, [matches]);


  const registerTournament =
    async () => {

      if (!token) {
        navigate('/login');
        return;
      }

      if (!tournament) {
        return;
      }


      try {

        setRegistering(true);
        setMessage('');


        const response =
          await fetch(
            `${API_BASE}/big-tournaments/${id}/register`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },
            }
          );


        const data =
          await response.json();


        if (
          !response.ok ||
          !data?.ok
        ) {
          throw new Error(
            data?.error ||
            translate('bigTournaments.details.registrationError')
          );
        }


        if (
          data.balance !== undefined &&
          data.balance !== null
        ) {
          window.dispatchEvent(
            new CustomEvent(
              'balance-updated',
              {
                detail: {
                  balance:
                    Number(
                      data.balance
                    ),

                  source:
                    'big-tournament-register',

                  allowDecrease:
                    true,
                },
              }
            )
          );
        }


        const paid =
          Number(
            tournament.buy_in || 0
          );

        setMessage(
          paid > 0
            ? translate(
                'bigTournaments.details.registeredPaid',
                { amount: paid }
              )
            : translate(
                'bigTournaments.details.registeredFree'
              )
        );

        await loadData(true);

      } catch (e) {

        setMessage(
          e.message ||
          translate('bigTournaments.details.registrationFailed')
        );

      } finally {

        setRegistering(false);
      }
  };



  const performRebuy =
    async () => {

      if (!token) {
        navigate('/login');
        return;
      }

      if (
        !tournament ||
        tournament.mode !== 'rebuy'
      ) {
        return;
      }


      try {

        setRebuying(true);
        setMessage('');


        const response =
          await fetch(
            `${API_BASE}/big-tournaments/${id}/rebuy`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },
            }
          );


        const data =
          await response.json();


        if (
          !response.ok ||
          !data?.ok
        ) {
          throw new Error(
            data?.error ||
            translate('bigTournaments.details.rebuyFailed')
          );
        }


        if (
          data.balance !== undefined &&
          data.balance !== null
        ) {
          window.dispatchEvent(
            new CustomEvent(
              'balance-updated',
              {
                detail: {
                  balance:
                    Number(
                      data.balance
                    ),

                  source:
                    'big-tournament-rebuy',

                  allowDecrease:
                    true,
                },
              }
            )
          );
        }


        setMessage(
          translate(
            'bigTournaments.details.rebuySuccess',
            {
              amount: Number(data.cost || 0),
            }
          )
        );


        await loadData(true);


      } catch (e) {

        setMessage(
          e.message ||
          translate('bigTournaments.details.rebuyFailed')
        );

      } finally {

        setRebuying(false);
      }
  };


  if (loading) {
    return (
      <div className="big-tournament-details-state">
        {translate('bigTournaments.details.loading')}
      </div>
    );
  }


  if (error || !tournament) {
    return (
      <div className="big-tournament-details-state">
        <h2>{translate('bigTournaments.common.error')}</h2>

        <p>
          {error || translate('bigTournaments.details.notFound')}
        </p>

        <button
          onClick={() =>
            navigate('/big-tournaments')
          }
        >
          {translate('bigTournaments.common.back')}
        </button>
      </div>
    );
  }


  const maxPlayers =
    tournament.max_players;

  const registeredCount =
    Number(
      tournament.registeredCount || 0
    );


  const isFull =
    maxPlayers !== null &&
    maxPlayers !== undefined &&
    registeredCount >=
      Number(maxPlayers);


  const registrationOpen =
    tournament.status ===
      'registration' &&
    new Date(
      tournament.start_at
    ).getTime() > now &&
    !isFull;


  const rebuyWindow =
    tournament.rebuyWindow || null;


  const rebuyEndsAtMs =
    rebuyWindow?.endsAt
      ? new Date(
          rebuyWindow.endsAt
        ).getTime()
      : 0;


  const rebuyWindowOpen =
    tournament.mode === 'rebuy' &&
    tournament.status ===
      'rebuy_window' &&
    rebuyWindow &&
    !rebuyWindow.expired &&
    rebuyEndsAtMs > now;


  const rebuyRoundOpen =
    tournament.mode === 'rebuy' &&
    tournament.status ===
      'rebuy_round';


  const rebuyPhaseActive =
    rebuyRoundOpen ||
    rebuyWindowOpen;


  const rebuyCost =
    Number(
      rebuyWindow?.cost ??
      tournament.activeRebuyRule?.cost ??
      0
    );


  const normalizedGameType =
    String(
      tournament.game_type ||
      tournament.gameType ||
      'long'
    ).toLowerCase();


  const isShortTournament =
    normalizedGameType === 'short';


  const gameTypeLabel =
    isShortTournament
      ? translate('bigTournaments.common.shortBackgammon')
      : translate('bigTournaments.common.longBackgammon');


  const prizePool =
    Number(
      tournament.current_prize_pool ??
      tournament.guaranteed_prize_pool ??
      0
    );


  const configuredPrizeRules =
    parseArrayConfig(
      tournament.prize_rules
    );


  const matchedPrizeRule =
    configuredPrizeRules.find(
      rule => {
        const min =
          Number(rule?.minPlayers || 0);

        const max =
          rule?.maxPlayers === null ||
          rule?.maxPlayers === undefined
            ? Infinity
            : Number(rule.maxPlayers);

        return (
          registeredCount >= min &&
          registeredCount <= max
        );
      }
    ) || null;


  const resolvedPrizeRule =
    parseObjectConfig(
      tournament.resolved_prize_rules
    );


  const activePrizeRule =
    parseObjectConfig(
      tournament.activePrizeRule
    );


  const displayedPrizeRule =
    resolvedPrizeRule ||
    activePrizeRule ||
    matchedPrizeRule;


  const prizeDistribution =
    Array.isArray(
      displayedPrizeRule?.places
    )
      ? displayedPrizeRule.places
      : [];


  const calculatedPrizePlaces =
    prizeDistribution.reduce(
      (maximum, place) =>
        Math.max(
          maximum,
          Number(
            place?.to ??
            place?.place ??
            place?.from ??
            0
          )
        ),
      0
    );


  const prizePlaces =
    Number(
      tournament.prizePlaces ||
      calculatedPrizePlaces ||
      0
    );


  const rebuyRules =
    parseArrayConfig(
      tournament.rebuy_config
    )
      .slice()
      .sort(
        (a, b) =>
          Number(a?.round || 0) -
          Number(b?.round || 0)
      );


  const prizePoolTypeLabel =
    tournament.prize_pool_type ===
      'guaranteed'
      ? translate('bigTournaments.common.guaranteed')
      : translate('bigTournaments.common.dynamic');


  const canMyPlayerRebuy =
    rebuyPhaseActive &&
    myPlayer?.status ===
      'eliminated_rebuy';


  const myRebuyDone =
    rebuyPhaseActive &&
    myPlayer?.status ===
      'rebought';


  return (
    <div className="big-tournament-details">

      <div className="big-tournament-details__inner">

        <button
          type="button"
          className="big-tournament-back"
          onClick={() =>
            navigate('/big-tournaments')
          }
        >
          ← {translate('bigTournaments.details.allTournaments')}
        </button>


        <section className="big-tournament-details-hero">

          <div className="big-tournament-details-hero__main">

            <div className="big-tournament-details-badges">

              <span
                className={`
                  big-tournament-status
                  big-tournament-status--${tournament.status}
                `}
              >
                {statusLabels[
                  tournament.status
                ] || tournament.status}
              </span>

              <span className="big-tournament-details-id">
                {translate(
                  'bigTournaments.details.tournamentNumber',
                  { id: tournament.id }
                )}
              </span>

            </div>


            <h1>
              {tournament.name}
            </h1>


            <div className="big-tournament-details-type">
              {gameTypeLabel}
              <span>•</span>
              {tournament.mode === 'rebuy'
                ? 'Rebuy'
                : 'Knockout'}
            </div>


            {tournament.status ===
              'registration' && (
              <div className="big-tournament-details-countdown">

                <span>
                  {translate('bigTournaments.common.untilStart')}
                </span>

                <strong>
                  {formatCountdown(
                    tournament.start_at,
                    now
                  )}
                </strong>

              </div>
            )}

          </div>


          <div className="big-tournament-details-actions">

            {myActiveMatch?.table_id && (
              <button
                type="button"
                className="big-tournament-play-button"
                onClick={() => {
                  const tableId =
                    myActiveMatch.table_id;

                  navigate(
                    isShortTournament
                      ? `/tournamentMatch/${tableId}?tableId=${tableId}`
                      : `/big-tournament/table?tableId=${tableId}`
                  );
                }}
              >
                {translate('bigTournaments.details.goToTable')}
              </button>
            )}


            {!amRegistered &&
              registrationOpen && (
              <button
                type="button"
                disabled={registering}
                className="big-tournament-register-button"
                onClick={
                  registerTournament
                }
              >
                {registering
                  ? translate('bigTournaments.details.registering')
                  : translate('bigTournaments.details.register')}
              </button>
            )}


            {amRegistered && myPlayer && (
              <div
                className="big-tournament-registered"
                style={
                  myPlayer.status === 'eliminated'
                    ? {
                        color: '#b6bdc7',
                        background: 'rgba(255,255,255,.04)',
                        borderColor: 'rgba(255,255,255,.10)',
                      }
                    : myPlayer.status === 'eliminated_rebuy'
                      ? {
                          color: '#e0b65e',
                          background: 'rgba(211,168,84,.08)',
                          borderColor: 'rgba(211,168,84,.24)',
                        }
                      : myPlayer.status === 'finished' &&
                          Number(myPlayer.final_place || 0) === 1
                        ? {
                            color: '#e3bd70',
                            background: 'rgba(211,166,82,.11)',
                            borderColor: 'rgba(211,166,82,.28)',
                          }
                        : undefined
                }
              >
                {myPlayer.status === 'eliminated'
                  ? playerStatusLabels.eliminated
                  : myPlayer.status === 'eliminated_rebuy'
                    ? `↻ ${playerStatusLabels.eliminated_rebuy}`
                    : myPlayer.status === 'finished' &&
                        Number(myPlayer.final_place || 0) === 1
                      ? `🏆 #1 · ${playerStatusLabels.finished}`
                      : myPlayer.status === 'finished'
                        ? playerStatusLabels.finished
                        : `✓ ${translate('bigTournaments.details.participating')}`}
              </div>
            )}

          </div>

        </section>


        {rebuyPhaseActive &&
          (rebuyWindow ||
            tournament.activeRebuyRule) && (

          <section
            className={`
              big-tournament-rebuy-window
              ${canMyPlayerRebuy
                ? 'big-tournament-rebuy-window--available'
                : ''}
            `}
          >

            <div className="big-tournament-rebuy-window__main">

              <span className="big-tournament-rebuy-window__eyebrow">
                REBUY WINDOW
              </span>


              {canMyPlayerRebuy ? (
                <>
                  <h2>
                    {translate('bigTournaments.details.rebuyAvailable')}
                  </h2>

                  <p>
                    {rebuyRoundOpen
                      ? translate('bigTournaments.details.rebuyAvailableNow')
                      : translate('bigTournaments.details.rebuyFinalDecision')}
                  </p>
                </>
              ) : myRebuyDone ? (
                <>
                  <h2>
                    {translate('bigTournaments.details.rebuyCompleted')}
                  </h2>

                  <p>
                    {translate('bigTournaments.details.rebuyCompletedText')}
                  </p>
                </>
              ) : (
                <>
                  <h2>
                    {translate('bigTournaments.details.rebuyWindowOpen')}
                  </h2>

                  <p>
                    {rebuyRoundOpen
                      ? translate('bigTournaments.details.rebuyMatchesRunning')
                      : translate('bigTournaments.details.rebuyWaitForWindow')}
                  </p>
                </>
              )}

            </div>


            <div className="big-tournament-rebuy-window__side">

              <div className="big-tournament-rebuy-window__stat">

                <span>
                  {translate('bigTournaments.common.cost')}
                </span>

                <strong>
                  {rebuyCost}
                  {' '}
                  <small>
                    $
                  </small>
                </strong>

              </div>


              <div className="big-tournament-rebuy-window__stat">

                <span>
                  {rebuyRoundOpen
                    ? translate('bigTournaments.details.finalWindow')
                    : translate('bigTournaments.details.remaining')}
                </span>

                <strong className="big-tournament-rebuy-window__timer">
                  {rebuyRoundOpen
                    ? translate(
                        'bigTournaments.countdown.secondsAfterLastMatch',
                        {
                          seconds: Number(
                            tournament.activeRebuyRule?.windowSeconds ||
                            60
                          ),
                        }
                      )
                    : formatRebuyCountdown(
                        rebuyWindow?.endsAt,
                        now
                      )}
                </strong>

              </div>


              {canMyPlayerRebuy && (
                <button
                  type="button"
                  className="big-tournament-rebuy-button"
                  disabled={
                    rebuying ||
                    !rebuyPhaseActive
                  }
                  onClick={
                    performRebuy
                  }
                >
                  {rebuying
                    ? 'Rebuy...'
                    : translate(
                        'bigTournaments.details.returnFor',
                        { amount: rebuyCost }
                      )}
                </button>
              )}


              {myRebuyDone && (
                <div className="big-tournament-rebuy-done">
                  ✓ {translate('bigTournaments.details.returnedToTournament')}
                </div>
              )}

            </div>

          </section>
        )}


        {message && (
          <div className="big-tournament-message">
            {message}
          </div>
        )}


        <section className="big-tournament-summary">

          <div>
            <span>{translate('bigTournaments.common.start')}</span>
            <strong>
              {formatDate(
                tournament.start_at
              )}
            </strong>
          </div>

          <div>
            <span>{translate('bigTournaments.common.participants')}</span>
            <strong>
              {registeredCount}
              {' / '}
              {maxPlayers || '∞'}
            </strong>
          </div>

          <div>
            <span>{translate('bigTournaments.common.minimum')}</span>
            <strong>
              {tournament.min_players}
            </strong>
          </div>

          <div>
            <span>{translate('bigTournaments.common.prizePool')}</span>
            <strong>
              {formatCredits(prizePool)} $
            </strong>
          </div>

          <div>
            <span>{translate('bigTournaments.common.prizePlaces')}</span>
            <strong>
              {prizePlaces || '—'}
            </strong>
          </div>

          <div>
            <span>{translate('bigTournaments.common.currentRound')}</span>
            <strong>
              {tournament.current_round || '—'}
            </strong>
          </div>

        </section>


        <div className="big-tournament-tabs">

          <button
            className={
              activeTab === 'bracket'
                ? 'active'
                : ''
            }
            onClick={() =>
              setActiveTab('bracket')
            }
          >
            {translate('bigTournaments.details.tabs.bracket')}
          </button>

          <button
            className={
              activeTab === 'players'
                ? 'active'
                : ''
            }
            onClick={() =>
              setActiveTab('players')
            }
          >
            {translate('bigTournaments.common.participants')}
            <span>
              {players.length}
            </span>
          </button>

          <button
            className={
              activeTab === 'info'
                ? 'active'
                : ''
            }
            onClick={() =>
              setActiveTab('info')
            }
          >
            {translate('bigTournaments.details.tabs.about')}
          </button>

        </div>


        {activeTab === 'bracket' && (

          <section className="big-tournament-bracket-section">

            {rounds.length === 0 ? (

              <div className="big-tournament-empty">
                {translate('bigTournaments.details.bracketAfterStart')}
              </div>

            ) : (

              <div className="big-tournament-bracket-scroll">

                <div className="big-tournament-bracket">

                  {rounds.map(
                    ({
                      roundNumber,
                      matches: roundMatches
                    }) => (

                      <div
                        key={roundNumber}
                        className="big-tournament-round"
                      >

                        <div className="big-tournament-round__head">

                          <span>
                            {translate(
                              'bigTournaments.details.roundLabel',
                              { round: roundNumber }
                            )}
                          </span>

                          <strong>
                            {getRoundTitle(
                              roundMatches,
                              roundNumber
                            )}
                          </strong>

                        </div>


                        <div className="big-tournament-round__matches">

                          {roundMatches.map(
                            match => {

                              const p1Winner =
                                Number(
                                  match.winner_id
                                ) ===
                                Number(
                                  match.player1_id
                                );

                              const p2Winner =
                                Number(
                                  match.winner_id
                                ) ===
                                Number(
                                  match.player2_id
                                );

                              const isMine =
                                match.player1_name === username ||
                                match.player2_name === username;


                              return (
                                <div
                                  key={match.id}
                                  className={`
                                    big-tournament-match
                                    ${isMine
                                      ? 'big-tournament-match--mine'
                                      : ''}
                                    ${match.status === 'active'
                                      ? 'big-tournament-match--active'
                                      : ''}
                                  `}
                                >

                                  <div className="big-tournament-match__meta">

                                    <span>
                                      {translate(
                                        'bigTournaments.details.matchNumber',
                                        { id: match.id }
                                      )}
                                    </span>

                                    {match.is_bye ? (
                                      <b>BYE</b>
                                    ) : (
                                      <b>
                                        {match.status === 'active'
                                          ? 'LIVE'
                                          : match.status === 'finished'
                                            ? translate('bigTournaments.details.completedUpper')
                                            : match.status}
                                      </b>
                                    )}

                                  </div>


                                  <div
                                    className={`
                                      big-tournament-match-player
                                      ${p1Winner
                                        ? 'winner'
                                        : ''}
                                    `}
                                  >

                                    <span>
                                      {match.player1_name ||
                                        '—'}
                                    </span>

                                    {p1Winner && (
                                      <strong>✓</strong>
                                    )}

                                  </div>


                                  <div
                                    className={`
                                      big-tournament-match-player
                                      ${p2Winner
                                        ? 'winner'
                                        : ''}
                                    `}
                                  >

                                    <span>
                                      {match.is_bye
                                        ? 'BYE'
                                        : (
                                          match.player2_name ||
                                          translate('bigTournaments.common.waiting')
                                        )}
                                    </span>

                                    {p2Winner && (
                                      <strong>✓</strong>
                                    )}

                                  </div>

                                </div>
                              );
                            }
                          )}

                        </div>

                      </div>
                    )
                  )}

                </div>

              </div>
            )}

          </section>
        )}


        {activeTab === 'players' && (

          <section className="big-tournament-players">

            {tournament.mode === 'rebuy' &&
              reboughtPlayers.length > 0 && (

              <div className="big-tournament-rebought-list">

                <div className="big-tournament-rebought-list__head">
                  <div>
                    <span>REBUY</span>
                    <h3>{translate('bigTournaments.details.returnedPlayers')}</h3>
                  </div>

                  <strong>
                    {reboughtPlayers.length}
                  </strong>
                </div>

                <div className="big-tournament-rebought-list__rows">
                  {reboughtPlayers.map(
                    player => (
                      <div
                        key={`rebuy-${player.id}`}
                        className="big-tournament-rebought-row"
                      >
                        <strong>
                          {player.username}
                        </strong>

                        <span>
                          Rebuy: {Number(player.rebuy_count || 0)}
                        </span>

                        <span>
                          {translate(
                            'bigTournaments.details.lastRebuyRound',
                            {
                              round: Number(
                                player.last_rebuy_round ||
                                0
                              ),
                            }
                          )}
                        </span>
                      </div>
                    )
                  )}
                </div>

              </div>
            )}


            {players.length === 0 ? (

              <div className="big-tournament-empty">
                {translate('bigTournaments.details.noPlayers')}
              </div>

            ) : (

              players.map(
                (player, index) => (

                  <div
                    key={player.id}
                    className={`
                      big-tournament-player-row
                      ${player.username === username
                        ? 'big-tournament-player-row--me'
                        : ''}
                    `}
                  >

                    <span className="big-tournament-player-number">
                      {index + 1}
                    </span>

                    <strong>
                      {player.username}
                    </strong>

                    <span>
                      {playerStatusLabels[
                        player.status
                      ] || player.status}
                    </span>

                    <span>
                      {Number(
                        player.prize_amount || 0
                      ) > 0
                        ? (
                          translate(
                            'bigTournaments.details.playerPrize',
                            {
                              amount: Number(
                                player.prize_amount || 0
                              ),
                            }
                          )
                        )
                        : (
                          translate(
                            'bigTournaments.details.playerRound',
                            {
                              round:
                                player.current_round ||
                                0,
                            }
                          )
                        )}
                    </span>

                  </div>
                )
              )
            )}

          </section>
        )}


        {activeTab === 'info' && (

          <section className="big-tournament-info-panel">

            <div>
              <span>{translate('bigTournaments.common.format')}</span>
              <strong>
                {tournament.mode === 'rebuy'
                  ? 'Rebuy'
                  : 'Knockout'}
              </strong>
            </div>

            <div>
              <span>{translate('bigTournaments.common.game')}</span>
              <strong>
                {gameTypeLabel}
              </strong>
            </div>

            <div>
              <span>{translate('bigTournaments.common.minPlayers')}</span>
              <strong>
                {tournament.min_players}
              </strong>
            </div>

            <div>
              <span>{translate('bigTournaments.common.maxPlayers')}</span>
              <strong>
                {tournament.max_players ||
                  translate('bigTournaments.common.noLimit')}
              </strong>
            </div>

            <div>
              <span>{translate('bigTournaments.common.prizePool')}</span>
              <strong>
                {formatCredits(prizePool)} $
              </strong>
            </div>

            <div>
              <span>{translate('bigTournaments.details.fundType')}</span>
              <strong>
                {prizePoolTypeLabel}
              </strong>
            </div>

            <div>
              <span>{translate('bigTournaments.common.prizePlaces')}</span>
              <strong>
                {prizePlaces || '—'}
              </strong>
            </div>

            <div
              style={{
                gridColumn: '1 / -1'
              }}
            >
              <span>{translate('bigTournaments.details.prizeDistribution')}</span>

              <strong
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {prizeDistribution.length > 0
                  ? prizeDistribution.map(
                      (place, index) => (
                        <span
                          key={`prize-place-${index}`}
                        >
                          {formatPrizePlaces(place)}
                          {' — '}
                          {formatCredits(
                            place.percent
                          )}%
                        </span>
                      )
                    )
                  : (
                    <span>
                      {translate('bigTournaments.details.distributionNotSpecified')}
                    </span>
                  )}
              </strong>
            </div>

            {tournament.mode === 'rebuy' && (
              <div
                style={{
                  gridColumn: '1 / -1'
                }}
              >
                <span>{translate('bigTournaments.details.rebuyByRounds')}</span>

                <strong
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {rebuyRules.length > 0
                    ? rebuyRules.map(
                        (rule, index) => (
                          <span
                            key={`rebuy-rule-${index}`}
                          >
                            {translate(
                              'bigTournaments.details.roundLabel',
                              {
                                round: Number(
                                  rule.round ||
                                  index + 1
                                ),
                              }
                            )}
                            {' — '}
                            {formatCredits(
                              rule.cost
                            )} $
                            {'; '}
                            {translate(
                              'bigTournaments.details.windowAfterLastMatch',
                              {
                                seconds: Number(
                                  rule.windowSeconds ||
                                  60
                                ),
                              }
                            )}
                          </span>
                        )
                      )
                    : (
                      <span>
                        {translate('bigTournaments.details.rebuyRulesNotSpecified')}
                      </span>
                    )}
                </strong>
              </div>
            )}

            <div>
              <span>{translate('bigTournaments.common.status')}</span>
              <strong>
                {statusLabels[
                  tournament.status
                ] || tournament.status}
              </strong>
            </div>

          </section>
        )}

      </div>
    </div>
  );
}
